import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { MenuPopover } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';

export function PatientTabIconButton({
  label,
  className,
  children,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  label: string;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      className={cn('h-8 w-8 min-h-8 min-w-8', className)}
      {...props}
    >
      {children}
    </Button>
  );
}

export function PatientTabMoreMenu({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const aria = label ?? t('patients.header.more_actions');
  return (
    <MenuPopover
      title={aria}
      trigger={
        <PatientTabIconButton label={aria}>
          <MoreHorizontal className="h-4 w-4" />
        </PatientTabIconButton>
      }
    >
      {children}
    </MenuPopover>
  );
}

export function ConfirmActionModal({
  open,
  title,
  description,
  confirmLabel,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      tone="danger"
      showIcon
      role="alertdialog"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {null}
    </Modal>
  );
}
